'use client'

import { useEffect, useState } from 'react'
import { useSignIn } from '@clerk/nextjs'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { otpSchema, type OtpFormValues } from '@/features/auth/schemas/auth.schema'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'

const RESEND_COOLDOWN_SECONDS = 30

/**
 * First of two reset-password steps — deliberately its own component, not combined with the
 * new-password step, so only one set of fields (the code) is ever on screen at a time. Field
 * styling mirrors `login-view.tsx`'s phone-OTP step exactly (digit-only, centered, tracked).
 */
export function ResetPasswordCodeStep({
  email,
  onVerified,
}: {
  email: string
  onVerified: () => void
}) {
  const { signIn } = useSignIn()
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [infoMessage, setInfoMessage] = useState<string | null>(null)
  // Starts as soon as this step mounts — the dialog just sent the first code, so a resend
  // wouldn't do anything different for at least this long anyway.
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS)

  useEffect(() => {
    if (cooldown === 0) return
    const timer = setInterval(() => setCooldown((seconds) => Math.max(0, seconds - 1)), 1000)
    return () => clearInterval(timer)
  }, [cooldown])

  const form = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    mode: 'onTouched',
    defaultValues: { otp: '' },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitting(true)
    setErrorMessage(null)
    setInfoMessage(null)
    try {
      const { error } = await signIn.resetPasswordEmailCode.verifyCode({ code: values.otp })
      if (error) {
        setErrorMessage('That code is incorrect or has expired.')
        return
      }
      onVerified()
    } finally {
      setSubmitting(false)
    }
  })

  const handleResend = async () => {
    setResending(true)
    setErrorMessage(null)
    setInfoMessage(null)
    try {
      // Clerk has no separate "resend" call — sending the code again is the resend.
      await signIn.resetPasswordEmailCode.sendCode()
      form.reset({ otp: '' })
      setCooldown(RESEND_COOLDOWN_SECONDS)
      setInfoMessage("We've sent a new code.")
    } catch {
      setErrorMessage('Something went wrong. Please try again.')
    } finally {
      setResending(false)
    }
  }

  return (
    <FieldGroup>
      <Field>
        <FieldLabel htmlFor="reset-code">Enter the 6-digit code</FieldLabel>
        <Input
          id="reset-code"
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="••••••"
          className="text-center text-lg font-semibold tracking-[8px]"
          aria-invalid={!!form.formState.errors.otp}
          {...form.register('otp', {
            onChange: (e) => {
              e.target.value = e.target.value.replace(/\D/g, '').slice(0, 6)
            },
          })}
        />
        <FieldError errors={[form.formState.errors.otp]} />
        <FieldDescription>We&apos;ve sent a verification code to {email}.</FieldDescription>
      </Field>
      {errorMessage && <FieldDescription className="text-red-600">{errorMessage}</FieldDescription>}
      {infoMessage && <FieldDescription>{infoMessage}</FieldDescription>}
      <Field>
        <Button type="button" onClick={onSubmit} disabled={submitting}>
          {submitting ? 'Verifying…' : 'Verify code'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={handleResend}
          disabled={resending || cooldown > 0}
        >
          {resending
            ? 'Sending…'
            : cooldown > 0
              ? `Resend code in ${cooldown}s`
              : "Didn't get a code? Resend"}
        </Button>
      </Field>
    </FieldGroup>
  )
}
